import { useState, useEffect, useCallback } from 'react'
import { api } from '../../../lib/api'

interface User {
  id: number
  email: string
  name: string | null
  subscription_tier: string
}

interface AuthState {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
}

export function useAuth() {
  const [state, setState] = useState<AuthState>(() => {
    const stored = localStorage.getItem('auth_user')
    const token = localStorage.getItem('auth_token')
    return {
      user: stored ? JSON.parse(stored) : null,
      isAuthenticated: !!token,
      isLoading: !!token,
    }
  })

  useEffect(() => {
    const token = localStorage.getItem('auth_token')
    if (!token) {
      setState({ user: null, isAuthenticated: false, isLoading: false })
      return
    }

    api
      .get('/auth/me')
      .then((data: User) => {
        localStorage.setItem('auth_user', JSON.stringify(data))
        setState({ user: data, isAuthenticated: true, isLoading: false })
      })
      .catch(() => {
        localStorage.removeItem('auth_token')
        localStorage.removeItem('auth_user')
        setState({ user: null, isAuthenticated: false, isLoading: false })
      })
  }, [])

  const login = useCallback(async (email: string, password: string) => {
    const data = await api.post('/auth/login', { email, password })
    localStorage.setItem('auth_token', data.access_token)
    localStorage.setItem('auth_user', JSON.stringify(data.user))
    setState({ user: data.user, isAuthenticated: true, isLoading: false })
    return data
  }, [])

  const register = useCallback(async (email: string, password: string, name?: string) => {
    const data = await api.post('/auth/register', { email, password, name })
    localStorage.setItem('auth_token', data.access_token)
    localStorage.setItem('auth_user', JSON.stringify(data.user))
    setState({ user: data.user, isAuthenticated: true, isLoading: false })
    return data
  }, [])

  const logout = useCallback(() => {
    localStorage.removeItem('auth_token')
    localStorage.removeItem('auth_user')
    setState({ user: null, isAuthenticated: false, isLoading: false })
  }, [])

  return { ...state, login, register, logout }
}
